import { useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { useApi } from '../../hooks/useApi';
import { TitleEditorModal } from './TitleEditorModal';
import { EpisodeManagerModal } from './EpisodeManagerModal';
import { Search, Plus, PlaySquare, Edit3, Star, Eye, Heart, Layers, Image as ImageIcon } from 'lucide-react';

export interface StudioEpisode {
  id: string;
  episodeNumber: number;
  title: string | null;
  durationSeconds: number;
  isFree: boolean;
  coinPrice: number;
  status: string;
  transcodeStatus: string;
  bunnyVideoId: string | null;
  thumbnailUrl: string | null;
}

export interface StudioSeason {
  id: string;
  seasonNumber: number;
  title: string | null;
  episodes: StudioEpisode[];
}

export interface StudioTitle {
  id: string;
  slug: string;
  title: string;
  subTitle: string | null;
  synopsis: string | null;
  genres: string[];
  cast: string[];
  director: string | null;
  releaseYear: number | null;
  status: string;
  featured: boolean;
  rating: number;
  themeColor: string | null;
  posterVerticalUrl: string | null;
  posterHorizontalUrl: string | null;
  stats: { views: number; likes: number } | null;
  seasons: StudioSeason[];
}

const STATUS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'accent' }> = {
  draft: { label: 'Draft', variant: 'default' },
  pending_review: { label: 'In review', variant: 'warning' },
  published: { label: 'Live', variant: 'success' },
  rejected: { label: 'Needs changes', variant: 'danger' },
};

const fmt = (n: number) => n.toLocaleString();

export function TitleCatalog({ scope }: { scope: 'mine' | 'all' }) {
  const titlesState = useApi<{ titles: StudioTitle[] }>('/admin/titles');
  const genresState = useApi<{ genres: { slug: string; label: string }[] }>('/admin/genres');

  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('all');
  const [editing, setEditing] = useState<StudioTitle | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [episodesTitleId, setEpisodesTitleId] = useState<string | null>(null);

  const genres = genresState.data?.genres ?? [];
  const titles = titlesState.data?.titles ?? [];
  const episodesTitle = titles.find((t) => t.id === episodesTitleId) ?? null;

  const openEditor = (t: StudioTitle | null) => {
    setEditing(t);
    setEditorKey((k) => k + 1);
    setEditorOpen(true);
  };

  const addButton = (
    <button
      onClick={() => openEditor(null)}
      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold shadow-lg shadow-violet-900/30 transition-colors"
    >
      <Plus className="w-4 h-4" />
      <span>Add title</span>
    </button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={scope === 'all' ? 'All titles' : 'My titles'}
        subtitle={
          scope === 'all'
            ? 'Every series on the platform, from all creators.'
            : 'Your series. Add a title, then add its episodes.'
        }
        actions={addButton}
      />

      <Async state={titlesState}>
        {() => {
          if (titles.length === 0) {
            return (
              <EmptyState
                title="No titles yet"
                message="Add your first title to get started."
                action={addButton}
              />
            );
          }
          const q = search.trim().toLowerCase();
          const filtered = titles.filter((t) => {
            const matchesSearch =
              !q || t.title.toLowerCase().includes(q) || (t.synopsis ?? '').toLowerCase().includes(q);
            const matchesGenre = genre === 'all' || t.genres.includes(genre);
            return matchesSearch && matchesGenre;
          });

          return (
            <>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="relative w-full sm:w-80">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by title or story…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-[#120d1a] border border-[#241b33] rounded-xl text-xs text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                  {[{ slug: 'all', label: 'All' }, ...genres].map((g) => (
                    <button
                      key={g.slug}
                      onClick={() => setGenre(g.slug === 'all' ? 'all' : g.label)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium shrink-0 transition-colors ${
                        genre === (g.slug === 'all' ? 'all' : g.label)
                          ? 'bg-violet-600 text-white'
                          : 'bg-[#120d1a] border border-[#241b33] text-gray-400 hover:text-white'
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {filtered.length === 0 ? (
                <EmptyState title="No titles match" message="Try a different search or category." />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filtered.map((item) => {
                    const epCount = item.seasons.reduce((n, s) => n + s.episodes.length, 0);
                    const cover = item.posterHorizontalUrl || item.posterVerticalUrl;
                    const status = STATUS[item.status] ?? { label: item.status, variant: 'default' as const };
                    return (
                      <div
                        key={item.id}
                        className="group bg-[#120d1a] border border-[#241b33] hover:border-violet-500/50 rounded-2xl overflow-hidden transition-all flex flex-col"
                      >
                        <div className="relative aspect-[16/10] bg-[#0a0712] overflow-hidden">
                          {cover ? (
                            <img src={cover} alt={item.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-[#0c0814] text-gray-600">
                              <ImageIcon className="w-8 h-8" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-[#120d1a] via-black/20 to-transparent" />
                          <div className="absolute top-2.5 left-2.5 flex gap-1.5">
                            <Badge variant={status.variant}>{status.label}</Badge>
                            {item.featured && <Badge variant="accent">Featured</Badge>}
                          </div>
                          <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between text-[11px] text-white/90">
                            <div className="flex items-center gap-1">
                              {item.rating > 0 && (
                                <>
                                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                  <span className="font-semibold">{item.rating}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-gray-300 text-[10px]">
                              <span className="flex items-center gap-0.5" title="Views">
                                <Eye className="w-3 h-3" />
                                <span>{item.stats ? fmt(item.stats.views) : '—'}</span>
                              </span>
                              <span className="flex items-center gap-0.5" title="Likes">
                                <Heart className="w-3 h-3 text-rose-400" />
                                <span>{item.stats ? fmt(item.stats.likes) : '—'}</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                          <div>
                            {item.genres.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {item.genres.slice(0, 3).map((g) => (
                                  <span key={g} className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-violet-300 font-medium">
                                    {g}
                                  </span>
                                ))}
                              </div>
                            )}
                            <h3 className="font-bold text-sm text-white line-clamp-1">{item.title}</h3>
                            <p className="text-gray-400 text-xs line-clamp-2 mt-1 leading-relaxed">
                              {item.synopsis || 'No story summary yet.'}
                            </p>
                          </div>
                          <div className="pt-3 border-t border-[#1e152d] flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-gray-300 flex items-center gap-1">
                              <Layers className="w-3.5 h-3.5 text-violet-400" />
                              <span>{epCount} {epCount === 1 ? 'episode' : 'episodes'}</span>
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => setEpisodesTitleId(item.id)}
                                className="px-2.5 py-1.5 rounded-lg bg-[#1a1228] hover:bg-violet-600 hover:text-white text-gray-300 text-xs font-medium border border-[#2c2140] transition-colors flex items-center gap-1"
                              >
                                <PlaySquare className="w-3.5 h-3.5" />
                                <span>Episodes</span>
                              </button>
                              <button
                                onClick={() => openEditor(item)}
                                className="px-2.5 py-1.5 rounded-lg bg-[#1a1228] hover:bg-[#251a3a] text-gray-300 hover:text-white border border-[#2c2140] text-xs font-medium transition-colors flex items-center gap-1"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                <span>Edit</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        }}
      </Async>

      {editorOpen && (
        <TitleEditorModal
          key={editorKey}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          title={editing}
          onSaved={titlesState.reload}
        />
      )}

      <EpisodeManagerModal
        open={episodesTitle !== null}
        onOpenChange={(o) => {
          if (!o) setEpisodesTitleId(null);
        }}
        title={episodesTitle}
        onChanged={titlesState.reload}
      />
    </div>
  );
}
